import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { GoldenEgg } from '@components/basics/GoldenEgg';
import { SongCard } from '@components/basics/SongCard';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type {
  FragmentType,
  NftSaleInfoFragmentDoc,
  PlaylistInput,
  PurchaseCardCollectedByUserFragmentDoc,
} from '@graphql/reactQuery';
import { getFragment, PurchaseCardFragmentDoc } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { ReleaseCardActions } from '../ReleaseCardActions';
import { useReleaseCardInfo } from '../useReleaseCardInfo';
import { CollectedBy } from './CollectedBy';
import { NftSaleInfo } from './NftSaleInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PurchaseCard on Release {
    id
    ...ReleasesCardInfo
    ...ReleaseCardActions
    ...ReleaseUpdates
  }
`);

export const PurchaseCard = ({
  releaseFrag,
  saleInfoFrag,
  collectedByFrag,
  isGoldenEgg,
  playlist,
}: {
  releaseFrag: FragmentType<PurchaseCardFragmentDoc>;
  saleInfoFrag?: FragmentType<NftSaleInfoFragmentDoc>;
  collectedByFrag?: FragmentType<PurchaseCardCollectedByUserFragmentDoc>[];
  isGoldenEgg?: boolean;
  playlist: PlaylistInput;
}) => {
  const release = getFragment(PurchaseCardFragmentDoc, releaseFrag);
  const {
    coverImageUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistName,
    artistWebappPath,
    releaseTitle,
  } = useReleaseCardInfo({ data: release, playlist });

  const {
    data: { totalMinted },
  } = useSubscribeRelease({
    release,
  });

  return (
    <Container>
      {isGoldenEgg && (
        <GoldenEggContainer>
          <GoldenEgg />
        </GoldenEggContainer>
      )}
      <SongCard
        album={isAlbum}
        isPlaying={isActiveTrack}
        loading={false}
        edition={false}
        expanded={false}
        setExpanded={null}
      >
        <SongCard.Cover
          coverLink={trackUrl}
          image={<SongCard.Cover.Single src={coverImageUrl} />}
          onPlayButtonClick={onPlayClick}
          isBufferingSong={isAudioBuffering}
          disablePlay={!hasAudioAccess}
          header={
            saleInfoFrag && (
              <SaleInfoContainer>
                <NftSaleInfo saleInfo={saleInfoFrag} />
              </SaleInfoContainer>
            )
          }
          playButtonPrefetchRef={playButtonPrefetchRef}
        />
        <SongCard.Info
          header={
            <AccessibleLink absoluteUri={trackUrl} type="external">
              {releaseTitle}
            </AccessibleLink>
          }
          subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
          actions={<ReleaseCardActions data={release} />}
          bottom={
            <div className="-mt-3 mb-1 flex h-11 w-full">
              {!!collectedByFrag && (
                <CollectedBy
                  collectedByFrag={collectedByFrag}
                  totalMinted={totalMinted}
                  releaseId={release.id}
                />
              )}
            </div>
          }
        />
      </SongCard>
    </Container>
  );
};

const SaleInfoContainer = styled('div', {
  position: 'absolute',
  top: 0,
  right: 0,
  left: 0,
});

const Container = styled('div', {
  position: 'relative',
});

export const BottomSection = styled('div', {
  display: 'flex',
  backgroundColor: '$neutral800',
  width: '100%',
  height: 47,
  boxShadow: '0px 8px 24px -8px rgba(0, 0, 0, 0.25)',

  '@tabletLandscape': {
    height: 56,
  },
});

const GoldenEggContainer = styled('div', {
  position: 'absolute',
  zIndex: '$above1',
  width: 52,
  height: 52,
  top: -20,
  right: 0,

  '@tabletLandscape': {
    width: 86,
    height: 86,
  },
});
